import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

type FocusLockActivationOptions = {
  containerRef: RefObject<HTMLElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  initialFocusDelay?: number;
};

const AUTOFOCUS_SELECTOR = '[data-autofocus]:not([data-autofocus="false"])';

/**
 * The `onActivation` handler for react-focus-lock. The lock calls it after it
 * records where to return focus, and before it looks for an element to focus.
 * While focus is still outside the lock, that look-up reads the computed style
 * of every focusable element on the page, which takes seconds on a large
 * page. Focus moved inside the lock here skips it.
 */
export function useFocusLockActivation({
  containerRef,
  initialFocusRef,
  initialFocusDelay = 320,
}: FocusLockActivationOptions) {
  const isOpeningRef = useRef(true);
  const initialFocusTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  return useCallback(
    (lockNode: HTMLElement) => {
      const isOpening = isOpeningRef.current;

      if (isOpening || !lockNode.contains(document.activeElement)) {
        const autoFocusTarget = lockNode.querySelector<HTMLElement>(
          AUTOFOCUS_SELECTOR
        );
        (autoFocusTarget ?? containerRef.current)?.focus({
          preventScroll: true,
        });
      }

      if (!isOpening) {
        return;
      }

      // A lock inside the overlay, such as a loading modal, can hold focus
      // when the timer fires. The lock activates again when that one closes,
      // and initial focus is tried again then.
      clearTimeout(initialFocusTimerRef.current);
      initialFocusTimerRef.current = setTimeout(() => {
        const target = initialFocusRef?.current;
        target?.focus();
        isOpeningRef.current =
          Boolean(target) && document.activeElement !== target;
      }, initialFocusDelay);
    },
    [containerRef, initialFocusRef, initialFocusDelay]
  );
}
